import React, { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { CheckCircle2, MessageCircle, Mail, Instagram } from "lucide-react";
import { toast } from "sonner";
import { fadeUp, staggerContainer, viewportOnce } from "@/lib/motion";
import { SunArcs } from "@/components/SunArcs";
import { Checkbox } from "@/components/ui/checkbox";
import { LegalLink } from "@/components/Legal";
import { WHATSAPP_URL, PHONE, EMAIL, INSTAGRAM_URL, INSTAGRAM_HANDLE } from "@/lib/scroll";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELDS = [
  { name: "nome", label: "Nome e cognome", type: "text", required: true },
  { name: "struttura", label: "Nome struttura (facoltativo)", type: "text", required: false },
  { name: "unita", label: "Numero camere / unità (facoltativo)", type: "text", required: false },
  { name: "cellulare", label: "Cellulare", type: "tel", required: true },
  { name: "email", label: "Email", type: "email", required: true },
  { name: "citta", label: "Città", type: "text", required: true },
];

export const Contatto = () => {
  const [form, setForm] = useState({ nome: "", struttura: "", unita: "", cellulare: "", email: "", citta: "", messaggio: "" });
  const [privacy, setPrivacy] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    for (const f of FIELDS) {
      if (f.required && !form[f.name].trim()) {
        toast.error("Compila tutti i campi obbligatori.");
        return;
      }
    }
    if (!/^\S+@\S+\.\S+$/.test(form.email.trim())) {
      toast.error("Inserisci un indirizzo email valido.");
      return;
    }
    if (!form.messaggio.trim()) {
      toast.error("Il messaggio è obbligatorio.");
      return;
    }
    if (!privacy) {
      toast.error("Per continuare devi accettare la Privacy Policy.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, { ...form, privacy });
      setSent(true);
      toast.success("Richiesta inviata! Ti ricontattiamo a breve.");
    } catch (err) {
      toast.error("Qualcosa è andato storto. Riprova o scrivici su WhatsApp.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contatti" data-testid="section-contatti" className="relative overflow-hidden bg-navy py-24 text-white md:py-32">
      <SunArcs className="pointer-events-none absolute -bottom-24 -left-16 w-[520px] max-w-[70vw]" rings={6} opacity={0.14} />

      <div className="relative mx-auto grid max-w-7xl grid-cols-1 gap-14 px-6 md:grid-cols-2 md:px-12">
        <motion.div variants={staggerContainer} initial="hidden" whileInView="show" viewport={viewportOnce}>
          <motion.p variants={fadeUp} className="mb-5 text-xs font-semibold uppercase tracking-[0.25em] text-gold">
            Iniziamo
          </motion.p>
          <motion.h2 variants={fadeUp} className="font-display text-4xl font-medium tracking-tight sm:text-5xl">
            Iniziamo dai tuoi numeri.
          </motion.h2>
          <motion.p variants={fadeUp} className="mt-7 max-w-md text-base leading-relaxed text-white/75 sm:text-lg">
            Raccontaci del tuo immobile. Ti restituiamo un'analisi gratuita e senza impegno: dove stai
            lasciando ricavi e cosa si può migliorare. Poi decidi tu.
          </motion.p>

          <motion.div variants={fadeUp} className="mt-10 space-y-4">
            <a
              data-testid="contact-whatsapp"
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-center gap-4 rounded-2xl border border-white/10 bg-white/[0.04] p-4 transition-colors duration-300 hover:border-gold/40"
            >
              <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[#25D366]/15 text-[#25D366]">
                <MessageCircle className="h-5 w-5" />
              </span>
              <span className="text-sm">
                <span className="block text-white/50">WhatsApp</span>
                <span className="font-medium text-white">{PHONE}</span>
              </span>
            </a>
            <a
              data-testid="contact-email"
              href={`mailto:${EMAIL}`}
              className="group flex items-center gap-4 rounded-2xl border border-white/10 bg-white/[0.04] p-4 transition-colors duration-300 hover:border-gold/40"
            >
              <span className="flex h-11 w-11 items-center justify-center rounded-full bg-gold/15 text-gold">
                <Mail className="h-5 w-5" />
              </span>
              <span className="text-sm">
                <span className="block text-white/50">Email</span>
                {/* confermare */}
                <span className="font-medium text-white">{EMAIL}</span>
              </span>
            </a>
            <a
              data-testid="contact-instagram"
              href={INSTAGRAM_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-center gap-4 rounded-2xl border border-white/10 bg-white/[0.04] p-4 transition-colors duration-300 hover:border-gold/40"
            >
              <span className="flex h-11 w-11 items-center justify-center rounded-full bg-amber/15 text-amber">
                <Instagram className="h-5 w-5" />
              </span>
              <span className="text-sm">
                <span className="block text-white/50">Instagram</span>
                <span className="font-medium text-white">{INSTAGRAM_HANDLE}</span>
              </span>
            </a>
          </motion.div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={viewportOnce}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="rounded-[2rem] border border-white/10 bg-white p-6 text-navy-deep shadow-[0_30px_60px_rgba(0,0,0,0.25)] md:p-8"
        >
          {sent ? (
            <div data-testid="contact-success" className="flex min-h-[420px] flex-col items-center justify-center text-center">
              <CheckCircle2 className="h-16 w-16 text-gold" />
              <h3 className="mt-6 font-display text-2xl font-semibold text-navy">Richiesta ricevuta.</h3>
              <p className="mt-3 max-w-xs text-text-muted">
                Grazie! Analizziamo il tuo immobile e ti ricontattiamo a breve con la tua analisi
                gratuita.
              </p>
              <button
                data-testid="contact-reset"
                onClick={() => setSent(false)}
                className="mt-8 rounded-full border border-navy/20 px-6 py-2.5 font-display text-sm font-semibold text-navy transition-colors duration-300 hover:border-gold hover:text-gold"
              >
                Invia un'altra richiesta
              </button>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="contact-form" className="space-y-5">
              {FIELDS.map((f) => (
                <div key={f.name}>
                  <label htmlFor={f.name} className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-navy/60">
                    {f.label}
                  </label>
                  <input
                    id={f.name}
                    data-testid={`field-${f.name}`}
                    type={f.type}
                    value={form[f.name]}
                    onChange={update(f.name)}
                    className="w-full rounded-xl border border-navy/15 bg-cream/60 px-4 py-3 text-navy-deep outline-none transition-colors duration-300 placeholder:text-navy/30 focus:border-gold focus:bg-white"
                  />
                </div>
              ))}
              <div>
                <label htmlFor="messaggio" className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-navy/60">
                  Messaggio
                </label>
                <textarea
                  id="messaggio"
                  data-testid="field-messaggio"
                  rows={3}
                  value={form.messaggio}
                  onChange={update("messaggio")}
                  className="w-full resize-none rounded-xl border border-navy/15 bg-cream/60 px-4 py-3 text-navy-deep outline-none transition-colors duration-300 placeholder:text-navy/30 focus:border-gold focus:bg-white"
                />
              </div>
              <div className="flex items-start gap-3 pt-1">
                <Checkbox
                  id="privacy"
                  data-testid="field-privacy"
                  checked={privacy}
                  onCheckedChange={(v) => setPrivacy(v === true)}
                  className="mt-0.5 border-navy/30 data-[state=checked]:border-gold data-[state=checked]:bg-gold data-[state=checked]:text-navy-deep"
                />
                <label htmlFor="privacy" className="text-sm leading-relaxed text-navy-deep">
                  Ho letto e accetto la{" "}
                  <LegalLink
                    type="privacy"
                    testId="privacy-link"
                    className="font-semibold text-gold underline underline-offset-2 hover:text-amber"
                    label="Privacy Policy"
                  />
                  .
                </label>
              </div>
              <motion.button
                type="submit"
                data-testid="contact-submit"
                disabled={loading || !privacy}
                whileHover={{ y: -2 }}
                whileTap={{ scale: 0.98 }}
                className="mt-2 w-full rounded-full bg-gold px-7 py-4 font-display text-base font-semibold text-navy-deep transition-colors duration-300 hover:bg-amber hover:text-white disabled:cursor-not-allowed disabled:opacity-60"
              >
                {loading ? "Invio in corso…" : "Richiedi l'analisi gratuita"}
              </motion.button>
              <p className="text-center text-xs text-navy/40">
                Analisi gratuita e senza impegno · Account sempre intestati al proprietario
              </p>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
};
